import React, { useState, useEffect } from 'react';
import { ShoppingCart, Tag } from 'lucide-react';
import type { Product } from '../core/ports';
import { useCart } from '../core/cart/CartContext';
import { formatPrice, isProductInStock, getStockStatusText, isPreOrder } from '../../utils/helpers';
import { Loading } from '../../components/ui/Loading';

interface ProductCardProps {
  product: Product;
  onViewDetails?: (product: Product) => void;
  className?: string;
}

export function ProductCard({ product, onViewDetails, className = '' }: ProductCardProps) {
  const { addToCart, isInCart, getCartItemQuantity, error: cartError, clearError } = useCart();
  const [imageLoading, setImageLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showError, setShowError] = useState(false);
  const cartQuantity = getCartItemQuantity(product.id);
  const remainingStock = product.stockQuantity !== undefined ? product.stockQuantity - cartQuantity : undefined;
  const preOrder = isPreOrder(product);
  const isInStock = isProductInStock(product.stockStatus, remainingStock);
  // Pre-orders are purchasable even when stock has not arrived yet
  const canPurchase = preOrder || isInStock;
  const isInCartState = isInCart(product.id);
  

  const handleAddToCart = async () => {
    if (!canPurchase) return;
    setAddingToCart(true);
    setShowError(false);
    clearError(); // Clear any previous errors
    try { 
      await addToCart(product, 1); 
      // Don't show popup here - let the useEffect handle it based on actual cart changes
    } catch (err) {
      setShowError(true);
    } finally { 
      setAddingToCart(false); 
    }
  };

  // Monitor cart errors for this specific product
  useEffect(() => {
    if (cartError && cartError.includes(product.name)) {
      setShowError(true);
    } else if (!cartError) {
      setShowError(false); // Clear error if cartError is cleared
    }
  }, [cartError, product.name]);


  return (
    <div className={`gears-card ${className}`}>
      <div className="card-banner">
        <button onClick={() => onViewDetails?.(product)} style={{ cursor: 'pointer', background: 'transparent' }} aria-label="View details">
          {imageLoading && (<div className="absolute inset-0 flex items-center justify-center" style={{ position: 'absolute', inset: 0 }}><Loading size="sm" /></div>)}
          <img
            src={product.images[0] || '/assets/placeholder-product.jpg'}
            alt={product.name}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
          />
        </button>
        
        {Boolean(product.onSale) && (
          <div className="card-time-wrapper">
            <Tag size={14} />
            <span>SALE</span>
          </div>
        )}

        {preOrder && (
          <div className="card-badge-preorder" style={{
            position: 'absolute', top: 8, right: 8, zIndex: 2,
            background: 'var(--tertiary)', color: 'var(--primary)',
            fontSize: 11, fontWeight: 700, padding: '4px 8px',
            borderRadius: 4, letterSpacing: '0.04em'
          }}>
            PRE-ORDER
          </div>
        )}
      </div>

      <div className="card-content" style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 120, justifyContent: 'flex-start', alignItems: 'flex-start' }}>
        <div className="card-title-wrapper" style={{ minHeight: 56, width: '100%', textAlign: 'left' }}>
          <p className="card-subtitle" style={{ textAlign: 'left' }}>
            {product.categories?.[0] || 'TCG'}
          </p>
          <h3
            className="h3 card-title"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textAlign: 'left',
              lineHeight: '1.4',
              minHeight: '2.8em',
              width: '100%',
              margin: 0,
              padding: 0
            }}
          >
            {product.name}
          </h3>
        </div>
        <div className="card-prize" style={{ alignSelf: 'flex-start' }}>
          {product.onSale && product.salePrice ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: '#14fefb' 
              }}>
                {formatPrice(product.salePrice)}
              </span>
              <span style={{ 
                fontSize: '14px', 
                color: '#999', 
                textDecoration: 'line-through' 
              }}>
                {formatPrice(product.regularPrice || product.price)}
              </span>
            </div>
          ) : (
            <>{formatPrice(product.price)}</>
          )}
        </div>
      </div>

      <div className="card-actions" style={{ marginTop: 'auto' }}>
        {product.type === 'variable' || (product as any).hasVariations ? (
          <button className="btn btn-primary" onClick={() => onViewDetails?.(product)}>
            <span>View Options</span>
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleAddToCart} disabled={!canPurchase || addingToCart}>
            <ShoppingCart size={18} />
            <span>{addingToCart ? 'Adding…' : (canPurchase ? (isInCartState ? `In Cart (${cartQuantity})` : (preOrder ? 'Pre Order' : 'Add to cart')) : 'Out of stock')}</span>
          </button>
        )}
      </div>
      
      {/* Error display for stock limits */}
      {showError && cartError && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
          <div className="flex items-center justify-between">
            <p className="text-red-600 text-xs">{cartError}</p>
            <button 
              onClick={() => { setShowError(false); clearError(); }} 
              className="text-red-600 hover:text-red-800 text-xs ml-2"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
    </div>
  );
}

export function CompactProductCard({ product, onAddToCart, className = '' }: { product: Product; onAddToCart?: (p: Product) => void; className?: string; }) {
  const { addToCart, getCartItemQuantity, error: cartError, clearError } = useCart();
  const cartQuantity = getCartItemQuantity(product.id);
  const remainingStock = product.stockQuantity !== undefined ? product.stockQuantity - cartQuantity : undefined;
  const preOrder = isPreOrder(product);
  const isInStock = isProductInStock(product.stockStatus, remainingStock);
  // Pre-orders are purchasable even when stock has not arrived yet
  const canPurchase = preOrder || isInStock;
  const [imageLoading, setImageLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  

  const handleAddToCart = async () => {
    if (!canPurchase) return;
    setAddingToCart(true);
    setShowError(false);
    clearError(); // Clear any previous errors
    try {
      await addToCart(product, 1);
      if (onAddToCart) onAddToCart(product); // Call external handler if provided
      // Don't show popup here - let the useEffect handle it based on actual cart changes
    } catch (err) {
      setShowError(true);
    } finally {
      setAddingToCart(false);
    }
  };

  // Monitor cart errors for this specific product
  useEffect(() => {
    if (cartError && cartError.includes(product.name)) {
      setShowError(true);
    } else if (!cartError) {
      setShowError(false); // Clear error if cartError is cleared
    }
  }, [cartError, product.name]);


  return (
    <div className={`group bg-primary  shadow-md overflow-hidden sm:hover:shadow-xl sm:hover:-translate-y-1 transition-all duration-500 ease-out flex flex-col h-full ${className}`}>
      <div className="relative w-full h-48 bg-primary overflow-hidden">
        {imageLoading && (<div className="absolute inset-0 flex items-center justify-center"><Loading size="sm" /></div>)}
        <img 
          src={product.images[0] || '/assets/placeholder-product.jpg'} 
          alt={product.name} 
          className={`w-full h-full object-contain transition-all duration-700 ease-out sm:group-hover:scale-110 ${imageLoading ? 'opacity-0' : 'opacity-100'}`} 
          onLoad={() => setImageLoading(false)} 
          onError={() => setImageLoading(false)} 
        />
        {product.onSale && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded transform -translate-x-2 opacity-0 sm:group-hover:translate-x-0 sm:group-hover:opacity-100 transition-all duration-300 ease-out">
            SALE
          </div>
        )}
        {preOrder && (
          <div className="absolute top-2 right-2 bg-tertiary text-primary text-xs font-bold px-2 py-1 rounded">
            PRE-ORDER
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1" style={{ alignItems: 'flex-start', textAlign: 'left' }}>
        {product.categories?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2" style={{ justifyContent: 'flex-start' }}>
            {product.categories.slice(0,2).map((c,i) => (
              <span key={i} className="text-xs bg-black text-white px-2 py-1  font-medium sm:group-hover:bg-tertiary sm:group-hover:scale-105 transition-all duration-200">
                {c}
              </span>
            ))}
          </div>
        )}
        <h3 className="font-medium text-tertiary text-sm mb-2 line-clamp-2 min-h-[2.5rem] transition-colors duration-200" style={{ textAlign: 'left', lineHeight: '1.4', width: '100%', margin: 0 }}>
          {product.name}
        </h3>
        <div className="text-xs text-tertiary mb-2 transition-colors duration-200">
          {preOrder ? 'Available to pre-order' : getStockStatusText(product.stockStatus, product.stockQuantity)}
        </div>
        <div className="flex items-center gap-2 mb-3">
          {product.onSale && product.salePrice ? (
            <>
              <span className="text-lg font-bold text-tertiary sm:group-hover:scale-105 transition-transform duration-200">
                {formatPrice(product.salePrice)}
              </span>
              <span className="text-xs text-tertiary/70 line-through transition-opacity duration-200">
                {formatPrice(product.regularPrice)}
              </span>
            </>
          ) : (
            <span className="text-lg font-bold text-tertiary sm:group-hover:scale-105 transition-transform duration-200">
              {formatPrice(product.price)}
            </span>
          )}
        </div>
        <div className="flex-1"></div>
        {product.type === 'variable' || (product as any).hasVariations ? (
          <button 
            onClick={() => onAddToCart ? onAddToCart(product) : undefined}
            className="w-full py-3 px-4 font-semibold transition-all duration-300 bg-primary text-tertiary hover:bg-tertiary hover:text-primary hover:scale-105 hover:shadow-lg border-2 border-tertiary"
          >
            <div className="flex items-center justify-center">View Options</div>
          </button>
        ) : (
          <button 
            onClick={handleAddToCart} 
            disabled={!canPurchase || addingToCart} 
            className={`w-full py-3 px-4  font-semibold transition-all duration-300 ${
              canPurchase 
                ? 'bg-primary text-tertiary hover:bg-tertiary hover:text-primary hover:scale-105 hover:shadow-lg border-2 border-tertiary' 
                : 'bg-gray-300 text-gray-600 cursor-not-allowed'
            }`}
          >
            <div className="flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 mr-2 sm:group-hover:scale-110 transition-transform duration-200" />
              {addingToCart ? 'Adding...' : (canPurchase ? (preOrder ? 'Pre Order' : 'Add to Cart') : 'Out of Stock')}
            </div>
          </button>
        )}
      </div>
      
      {/* Error display for stock limits */}
      {showError && cartError && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
          <div className="flex items-center justify-between">
            <p className="text-red-600 text-xs">{cartError}</p>
            <button 
              onClick={() => { setShowError(false); clearError(); }} 
              className="text-red-600 hover:text-red-800 text-xs ml-2"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ProductListItem({ product, onViewDetails, onAddToCart, className = '' }: { product: Product; onViewDetails?: (p: Product) => void; onAddToCart?: (p: Product) => void; className?: string; }) {
  const preOrder = isPreOrder(product);
  const isInStock = isProductInStock(product.stockStatus, product.stockQuantity);
  // Pre-orders are purchasable even when stock has not arrived yet
  const canPurchase = preOrder || isInStock;
  const [imageLoading, setImageLoading] = useState(true);
  return (
    <div className={`group bg-primary  shadow-md p-6 sm:hover:shadow-xl sm:hover:-translate-y-1 transition-all duration-500 ease-out ${className}`} style={{ textAlign: 'left' }}>
      <div className="flex gap-6">
        <div className="relative w-32 h-32 bg-primary  overflow-hidden flex-shrink-0">
          {imageLoading && (<div className="absolute inset-0 flex items-center justify-center"><Loading size="sm" /></div>)}
          <img 
            src={product.images[0] || '/assets/placeholder-product.jpg'} 
            alt={product.name} 
            className={`w-full h-full object-contain transition-all duration-700 ease-out sm:group-hover:scale-110 ${imageLoading ? 'opacity-0' : 'opacity-100'}`} 
            onLoad={() => setImageLoading(false)} 
            onError={() => setImageLoading(false)} 
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col" style={{ alignItems: 'flex-start', textAlign: 'left' }}>
          {(product.categories?.length > 0 || preOrder) && (
            <div className="flex flex-wrap gap-1 mb-2" style={{ justifyContent: 'flex-start' }}>
              {preOrder && (
                <span className="text-xs bg-tertiary text-primary px-2 py-1 font-bold">
                  PRE-ORDER
                </span>
              )}
              {product.categories?.slice(0,3).map((c,i) => (
                <span key={i} className="text-xs bg-black text-white px-2 py-1  font-medium sm:group-hover:bg-tertiary sm:group-hover:scale-105 transition-all duration-200">
                  {c}
                </span>
              ))}
            </div>
          )}
          <h3 className="font-semibold text-tertiary mb-2 line-clamp-2 transition-colors duration-200" style={{ textAlign: 'left', lineHeight: '1.4', width: '100%', margin: 0 }}>
            {product.name}
          </h3>
          <p className="text-sm text-tertiary mb-3 line-clamp-2 transition-colors duration-200">
            {product.shortDescription}
          </p>
          <div className="flex items-center gap-3 mb-3">
            {product.onSale && product.salePrice ? (
              <>
                <span className="text-xl font-bold text-tertiary sm:group-hover:scale-105 transition-transform duration-200">
                  {formatPrice(product.salePrice)}
                </span>
                <span className="text-sm text-tertiary/70 line-through transition-opacity duration-200">
                  {formatPrice(product.regularPrice)}
                </span>
              </>
            ) : (
              <span className="text-xl font-bold text-tertiary sm:group-hover:scale-105 transition-transform duration-200">
                {formatPrice(product.price)}
              </span>
            )}
          </div>
          <div className="text-sm text-tertiary mb-4 transition-colors duration-200">
            {preOrder ? 'Available to pre-order' : getStockStatusText(product.stockStatus, product.stockQuantity)}
          </div>
          <div className="flex-1"></div>
          <div className="flex gap-3">
            <button 
              onClick={() => onViewDetails?.(product)} 
              className="text-sm text-tertiary font-medium px-4 py-2 border border-tertiary hover:bg-tertiary hover:text-primary hover:scale-105 transition-all duration-200"
            >
              View Details
            </button>
            <button 
              onClick={() => canPurchase && onAddToCart?.(product)} 
              disabled={!canPurchase} 
              className={`text-sm font-semibold px-6 py-2  transition-all duration-300 ${
                canPurchase 
                  ? 'bg-primary text-tertiary hover:bg-tertiary hover:text-primary hover:scale-105 hover:shadow-lg border-2 border-tertiary' 
                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
              }`}
            >
              {canPurchase ? (preOrder ? 'Pre Order' : 'Add to Cart') : 'Out of Stock'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


